import { useEffect, useState } from 'react';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import { PageShell } from '../components/PageShell';
import { ApplicationsTable } from '../components/ApplicationsTable';
import { colors, cardStyle } from '../theme';
import { listApplications } from '../api';
import { usePlatformPermissions } from '../usePlatformPermissions';
import type { ApplicationSummary } from '../types';

export function ApplicationsPage({ filterType, title }: { filterType?: string; title?: string }) {
  const identityApi = useApi(identityApiRef);
  const { canDelete } = usePlatformPermissions();
  const [applications, setApplications] = useState<ApplicationSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    listApplications(identityApi)
      .then((apps) => {
        if (!cancelled) setApplications(apps);
      })
      .catch((err) => {
        if (!cancelled) setError((err as Error).message);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = applications && filterType ? applications.filter((a) => a.type === filterType) : applications;

  return (
    <PageShell>
      <h1 style={{ fontSize: 20, fontWeight: 700, color: colors.text, margin: 0, marginBottom: 20 }}>
        {title ?? 'My apps'}
      </h1>
      <div style={{ ...cardStyle, overflow: 'hidden' }}>
        {error ? (
          <div role="alert" style={{ padding: 24, color: colors.danger, fontSize: 14 }}>
            Couldn&apos;t load applications: {error}
          </div>
        ) : filtered === null ? (
          <div style={{ padding: 24, color: colors.textMuted, fontSize: 14 }}>Loading applications…</div>
        ) : (
          <ApplicationsTable
            applications={filtered}
            canDelete={canDelete}
            // Drop the row locally rather than refetching: Coolify's own
            // application list is eventually consistent right after a delete,
            // so an immediate refetch can return the app it just removed and
            // make it flicker back into the table.
            onDeleted={(deleted) =>
              setApplications((current) => (current ?? []).filter((a) => a.id !== deleted.id))
            }
            // Refetch rather than patch a timestamp locally: after a redeploy
            // Coolify is the source of truth for status and updatedAt, and a
            // guessed value would be wrong if the deploy came up unhealthy.
            onRedeployed={() => {
              listApplications(identityApi)
                .then(setApplications)
                .catch(() => {
                  /* leave the current list; the redeploy itself succeeded */
                });
            }}
          />
        )}
      </div>
    </PageShell>
  );
}
